import React, { useLayoutEffect, useRef, useMemo } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);

const PRODUCTS = ["HBB", "Baqati", "Hayyak", "Upgrade"];

const TargetPerPlanChart = ({ premiumData, standardData }) => {
  const chartRef = useRef(null);

  // 🔥 Transform API data (same structure as your service)
  const chartData = useMemo(() => {
    if (!premiumData && !standardData) return [];

    return PRODUCTS.map((product) => ({
      product,
      premiumTarget: premiumData?.[product]?.target ?? 0,
      premiumSales: premiumData?.[product]?.sales_count ?? 0,
      premiumPct: premiumData?.[product]?.achieved_percentage ?? 0,

      standardTarget: standardData?.[product]?.target ?? 0,
      standardSales: standardData?.[product]?.sales_count ?? 0,
      standardPct: standardData?.[product]?.achieved_percentage ?? 0,
    }));
  }, [premiumData, standardData]);

  useLayoutEffect(() => {
    if (!chartData.length) return;

    const chart = am4core.create("targetPerPlanChartDiv", am4charts.XYChart);
    chartRef.current = chart;
    chart.data = chartData;

    // X Axis
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "product";
    categoryAxis.renderer.cellStartLocation = 0.2;
    categoryAxis.renderer.cellEndLocation = 0.8;

    // Y Axis
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.min = 0;
    valueAxis.extraMax = 0.25;
    valueAxis.numberFormatter.numberFormat = "#,###";

    // SALES (Columns + % label)
    const createColumnSeries = (field, pctField, name, color) => {
      const series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = field;
      series.dataFields.categoryX = "product";
      series.name = name;

      series.columns.template.fill = am4core.color(color);
      series.columns.template.stroke = am4core.color(color);
      series.columns.template.width = am4core.percent(55);
      series.clustered = true;

      series.tooltipText = "{name}: {valueY.formatNumber('#,###')}";

      // % Label
      const labelBullet = series.bullets.push(new am4charts.LabelBullet());
      labelBullet.label.verticalCenter = "bottom";
      labelBullet.label.dy = -10;
      labelBullet.label.fontSize = 12;
      labelBullet.label.fontWeight = "700";

      labelBullet.label.adapter.add("text", (text, target) => {
        const val = target.dataItem?.dataContext?.[pctField] ?? 0;
        return val > 0 ? `${val}%` : "";
      });

      labelBullet.label.adapter.add("fill", (fill, target) => {
        const val = target.dataItem?.dataContext?.[pctField] ?? 0;

        if (val >= 100) return am4core.color("#2ecc71");
        if (val >= 80) return am4core.color("#f39c12");
        return am4core.color("#5B9BD5");
      });

      return series;
    };

    // TARGET (Line)
    const createLineSeries = (field, name, color) => {
      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = field;
      series.dataFields.categoryX = "product";
      series.name = name;

      series.stroke = am4core.color(color);
      series.strokeWidth = 3;
      series.tensionX = 1;
      series.fillOpacity = 0;
      series.zIndex = 10;

      const bullet = series.bullets.push(new am4charts.CircleBullet());
      bullet.circle.radius = 4;
      bullet.circle.fill = am4core.color("#fff");
      bullet.circle.strokeWidth = 2;
      bullet.circle.stroke = am4core.color(color);

      series.tooltipText = "{name}: {valueY.formatNumber('#,###')}";

      return series;
    };

    // Premium
    createLineSeries("premiumTarget", "Premium Target", "#5B9BD5");
    createColumnSeries(
      "premiumSales",
      "premiumPct",
      "Premium Sales",
      "#7DB8E8",
    );

    // Standard
    createLineSeries("standardTarget", "Standard Target", "#9B8EC4");
    createColumnSeries(
      "standardSales",
      "standardPct",
      "Standard Sales",
      "#F4A460",
    );

    chart.legend = new am4charts.Legend();
    chart.legend.position = "bottom";

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;
    chart.cursor.lineX.disabled = true;
    chart.cursor.behavior = "none";

    return () => {
      chart.dispose();
    };
  }, [chartData]);

  if (!chartData.length) return null;

  return (
    <div style={{ width: "100%" }}>
      <div id="targetPerPlanChartDiv" style={{ width: "100%", height: 440 }} />
    </div>
  );
};

export default TargetPerPlanChart;
